<template>
  <div>
    <!-- ページ画像一覧を取得中に表示するローダー -->
    <div v-if="isNothingContentsData" :class="$style.progressArea">
      <v-progress-circular
        :class="$style.progressArea__loadingIcon"
        indeterminate
        color="primary"
      ></v-progress-circular>
    </div>

    <!-- ページ画像一覧を取得後表示 -->
    <div v-show="!isNothingContentsData">
      <div v-if="!isPanel" class="text-xs-center">
        <v-btn
          :class="$style.showImageListButton"
          @click="isShowSelectPageDialog = true"
        >
          <span :class="$style.showImageListButton__text">
            {{ lang.image_indicate }}
          </span>
        </v-btn>
      </div>
      <div style="text-align: center" v-if="!isPanel">
        {{ lang.image_indicate_desc }}
      </div>

      <!-- リンク領域設定 -->
      <div>
        <div :class="$style.canvasArea">
          <canvas
            id="myContentsEdit__contentsImageCanvas"
            :class="{ [$style.canvasContainer]: isMounted }"
          ></canvas>

          <div class="text-xs-right" :style="deleteButtonWrapWidth">
            <v-btn
              @click="onClickDeleteButton"
              color="error"
              :disabled="isDisabled || !existActiveObject"
              :loading="isDelete"
            >
              {{ lang.delete }}
            </v-btn>
          </div>
        </div>
      </div>

      <!-- リンク先の設定 -->
      <v-card style="position: relative;">
        <v-radio-group v-model="linkType" row class="pa-2" v-if="!isPanel">
          <v-radio :label="lang.url" value="url"></v-radio>
          <v-radio
            :label="lang.page_in_contents"
            value="page_in_contents"
          ></v-radio>
        </v-radio-group>
        <div v-if="linkType === 'url'" class="pa-2">
          <h2>{{ lang.url }}</h2>
          <v-text-field
            :disabled="isDisabled"
            v-model="embedLinkUrl"
            @input="checkUrl"
            :error-messages="errorMessageInvalidUrl"
            outline
            single-line
          ></v-text-field>
        </div>
        <div v-if="linkType === 'page_in_contents'" class="pa-2">
          <v-btn
            @click="isShowSelectJumpPageDialog = true"
            :disabled="isDisabled || !existActiveObject"
          >
            {{ lang.select_page }}
          </v-btn>
          <div v-if="!isNil(destinationPage)">
            <span>{{ displayDestinationPage }}</span>
          </div>
          <div>
            <span :class="$style.errorMessage__noPageSelected">
              {{ errorMessageNoPageSelected }}
            </span>
          </div>
        </div>
        <v-layout wrap class="pa-2">
          <v-flex xs-12 class="text-xs-right">
            <v-btn
              @click="onClickSaveButton"
              :loading="isSaveLink"
              :disabled="
                isDisabled ||
                  (linkType === 'url' && isInvalidUrl) ||
                  (linkType === 'page_in_contents' && isNil(destinationPage)) ||
                  (!embedLinkUrl && isNil(destinationPage)) ||
                  !existActiveObject
              "
            >
              {{ lang.apply }}
            </v-btn>
          </v-flex>
        </v-layout>
        <div
          v-show="isDisabled || !existActiveObject"
          :class="$style.inputAreaDisable"
        ></div>
      </v-card>

      <div class="text-xs-right pt-3">
        <v-btn
          @click="onClickGeneratePreview"
          :disabled="!isEdit"
          :loading="isGeneratePreview"
        >
          {{ lang.preview_create }}
        </v-btn>
        <v-btn @click="openUrl(previewUrl)" :disabled="!previewUrl">
          {{ lang.preview_view }}
        </v-btn>
      </div>
      <div class="text-xs-right pt-1">
        <v-btn
          @click="onClickSaveSettingButton"
          :loading="isSaveSetting"
          color="info"
        >
          {{ lang.release }}
        </v-btn>
      </div>

      <!-- 画面に表示するページ画像選択ダイアログ -->
      <vc-share-page-list-dialog
        :is-show-dialog="isShowSelectPageDialog"
        :images="contentsPageImages"
        :is-back-cover-default-setting="isBackCoverDefaultSetting"
        :is-front-cover-default-setting="isFrontCoverDefaultSetting"
        :selected-page="currentContentsPage"
        @getContentsPageImage="getContentsPageImage"
        @updateIsShowSelectPageDialog="updateIsShowSelectPageDialog"
        :lang="lang"
        :is-add-link-page="true"
      >
      </vc-share-page-list-dialog>

      <!-- ジャンプ先のページ画像選択ダイアログ -->
      <vc-share-page-list-dialog
        :is-show-dialog="isShowSelectJumpPageDialog"
        :images="contentsPageImages"
        :is-back-cover-default-setting="isBackCoverDefaultSetting"
        :is-front-cover-default-setting="isFrontCoverDefaultSetting"
        :selected-page="destinationPage"
        :is-jump-destination-select="true"
        @setDestinationPage="setDestinationPage"
        @updateIsShowSelectJumpPageDialog="updateIsShowSelectJumpPageDialog"
        :lang="lang"
        :is-to-link-page="true"
      >
      </vc-share-page-list-dialog>
    </div>
  </div>
</template>

<script>
import { fabric } from "fabric";
import { doUnscramble } from "./assets/unscramble";
import pageListDialog from "./pageListDialog";
import _ from "lodash";
import ice from "../../../lib/ice";
import log from "loglevel";

const IMAGE_SIZE_XS = 250;
const IMAGE_SIZE_MD = 500;

export default {
  name: "vc-share-contents-add-link",
  components: {
    "vc-share-page-list-dialog": pageListDialog
  },
  props: {
    lang: {
      type: Object,
      default: () => ({
        image_indicate: "リンクを設定するページを選ぶ",
        image_indicate_desc: "エリアを選択後にリンク先を入力してください。",
        url: "URL",
        page_in_contents: "作品内のページ",
        select_page: "ページ選択",
        page: ":page_num",
        delete: "選択したエリアをクリア",
        apply: "適用",
        preview_create: "プレビューを作成する",
        preview_view: "プレビューを表示する",
        release: "保存",
        destination_page: "ページ :selected_num / :max_num",
        error_no_page_selected: "ページを選択してください。"
      })
    },
    addLinkCompleteUrl: {
      type: String,
      default: ""
    },
    identificationCode: {
      type: String,
      default: ""
    },
    isBackCoverDefaultSetting: {
      type: Boolean,
      default: false
    },
    isFrontCoverDefaultSetting: {
      type: Boolean,
      default: false
    },
    isPanel: {
      type: Boolean,
      default: false
    },
    isWebUploadContents: {
      type: Boolean,
      default: false
    },
    validationLang: {
      type: String,
      default: ""
    },

    // stateToProps
    previewUrl: {
      type: String,
      default: ""
    },

    // actionsToProps
    deleteContentsAddLink: {
      type: Function,
      default: () => {}
    },
    getContentsPageList: {
      type: Function,
      default: () => {}
    },
    postContentsAddLink: {
      type: Function,
      default: () => {}
    },
    saveContentsAddLink: {
      type: Function,
      default: () => {}
    },
    saveContentsLinkSettings: {
      type: Function,
      default: () => {}
    },
    saveContentsLinkSettingsPreview: {
      type: Function,
      default: () => {}
    }
  },
  data: () => ({
    // 画像と画像のスクランブル解除に必要な変数
    contentsPageImages: [],
    contentsAllPageCount: 0,
    draftContentsLinks: [],
    draftId: 0,
    scrambleWidth: 0,
    scrambleHeight: 0,
    resolution: 8, // スクランブル解除時の固定パラメーター

    changedPages: false,
    currentContentsPage: 0,
    currentImageAspectRatio: 0,
    currentImageOriginWidth: 0,
    currentImageOriginHeight: 0,
    currentImageDataUrl: "",
    currentSelectObject: null,
    destinationPage: null,
    embedLinkUrl: "",
    errorMessageInvalidUrl: "",
    errorMessageNoPageSelected: "",
    existActiveObject: false,
    fabricCanvasElement: null,
    isClearedSelect: false,
    isEdit: false,
    isStartCreate: false,
    isMounted: false,
    isInitialDisplay: true,
    isInvalidUrl: false,
    isNothingContentsData: true,
    isShowSelectPageDialog: false,
    isShowSelectJumpPageDialog: false,
    isDisabled: true,
    isSaveLink: false,
    isDelete: false,
    isGeneratePreview: false,
    isSaveSetting: false,
    linkType: "url",
    originX: 0,
    originY: 0,
    resizeWidth: 0,
    resizeHeight: 0,
    windowWidth: 0
  }),
  mounted() {
    this.getContentsPageList({
      identificationCode: this.identificationCode
    }).then(res => {
      if (res.status !== 0) {
        return;
      }

      this.draftId = res.draftId;
      this.scrambleWidth = res.scrambleWidth;
      this.scrambleHeight = res.scrambleHeight;
      this.draftContentsLinks = res.draftContentsLinks;
      this.contentsAllPageCount = res.imageUrls.length;
      this.$nextTick(() => this.unscrambleImages(res.imageUrls));
    });

    this.fabricCanvasElement = new fabric.Canvas(
      "myContentsEdit__contentsImageCanvas"
    );
    fabric.Object.prototype.set({
      transparentCorners: false,
      cornerSize: 20
    });
    this.isMounted = true;
    this.fabricCanvasElement.isDrawingMode = false;

    /* ------------------------ 矩形作成処理セット ------------------------ */
    // 矩形生成 上から順に実行される
    // マウスダウンの前に選択解除されるため、選択した要素があるかをselection:clearedで検知する
    this.fabricCanvasElement.on("selection:cleared", event => {
      this.selectionCleared(event);
    });
    this.fabricCanvasElement.on("mouse:down", event => {
      this.mouseDown(event);
    });
    this.fabricCanvasElement.on("mouse:up", event => {
      this.mouseUp(event);
    });

    // 作成した矩形に対し、操作を行うと実行される
    this.fabricCanvasElement.on("object:moving", event => {
      this.mouseMoving(event);
    });
    this.fabricCanvasElement.on("object:moved", () => {
      this.mouseMoved();
    });
    this.fabricCanvasElement.on("object:scaling", event => {
      this.mouseScaling(event);
    });
    this.fabricCanvasElement.on("object:scaled", event => {
      this.mouseScaled(event);
    });
    /* ------------------------------------------------------------------ */

    let timerId = 0;
    this.windowWidth = window.innerWidth;
    window.addEventListener("resize", () => {
      if (this.windowWidth === window.innerWidth) {
        // iOS、androidはメニューバーがスクロールで隠れて画面のリサイズが行われる
        // そのため、横幅が変更されていない場合はresize処理を止める
        return;
      }

      this.windowWidth = window.innerWidth;
      if (timerId > 0) {
        clearTimeout(timerId);
      }
      timerId = setTimeout(() => {
        this.resizeCanvas();
      }, 200);
    });

    if (!ice.isIce()) {
      window.onbeforeunload = e => {
        e.returnValue = this.lang.beforeunload_message;
        return this.lang.beforeunload_message;
      };
    }
  },
  methods: {
    /* ------------------------ fabric.jsの矩形関連処理 ------------------------ */
    selectionCleared(event) {
      this.isClearedSelect = true;
      // iOS、androidで表示したとき、スクロールで選択解除される場合がある
      // この場合、取得したeventに'e'プロパティが存在しないのを利用して判定する
      if (!event.hasOwnProperty("e")) {
        this.fabricCanvasElement.setActiveObject(this.currentSelectObject);
        this.fabricCanvasElement.renderAll();
        this.isClearedSelect = false;
      }

      // ページを切り替えた時に選択解除のイベントが走る場合があるためチェック
      if (this.changedPages) {
        this.isClearedSelect = false;
        this.changedPages = false;
      }
    },
    mouseDown(event) {
      // 矩形が選択されている or 適用ボタンを押していない矩形がある場合、矩形が生成できないようにする
      const isNoSettingLinkObject =
        this.fabricCanvasElement
          .getObjects()
          .filter(object => this.isNil(object.linkId)).length > 0;
      const activeObject = this.fabricCanvasElement.getActiveObject();
      this.existActiveObject = !this.isNil(activeObject);
      if (
        isNoSettingLinkObject ||
        this.isClearedSelect ||
        this.existActiveObject
      ) {
        this.isStartCreate = false;
        this.isClearedSelect = false;
      } else {
        this.isStartCreate = true;
      }

      if (this.existActiveObject) {
        this.setDataInInputArea(activeObject);
        this.currentSelectObject = activeObject;
      }

      // 領域選択時にURLの警告メッセージを出すか判定する
      if (this.existActiveObject && !this.isNil(activeObject.linkUrl)) {
        this.checkUrl(activeObject.linkUrl);
      } else {
        this.errorMessageInvalidUrl = "";
        this.isInvalidUrl = false;
        this.currentSelectObject = null;
      }

      const mousePointer = this.fabricCanvasElement.getPointer(event, false);
      this.originX = Math.floor(mousePointer.x);
      this.originY = Math.floor(mousePointer.y);
    },
    mouseUp(event) {
      let mouseDownPointer = this.fabricCanvasElement.getPointer(event, false);
      let activeObjectWidth = mouseDownPointer.x;
      let activeObjectHeight = mouseDownPointer.y;
      if (activeObjectWidth < 0) {
        activeObjectWidth = 0;
      }
      if (activeObjectWidth > this.fabricCanvasElement.width) {
        activeObjectWidth = this.fabricCanvasElement.width;
      }
      if (activeObjectHeight < 0) {
        activeObjectHeight = 0;
      }
      if (activeObjectHeight > this.fabricCanvasElement.height) {
        activeObjectHeight = this.fabricCanvasElement.height;
      }
      const isMinimumSize =
        Math.abs(activeObjectWidth - this.originX) < 100 &&
        Math.abs(activeObjectHeight - this.originY) < 100;
      let rectangle = null;
      let rectWidth = 100;
      let rectHeight = 100;
      if (!isMinimumSize) {
        rectWidth = Math.abs(activeObjectWidth - this.originX);
        rectHeight = Math.abs(activeObjectHeight - this.originY);
      }

      if (this.isStartCreate) {
        rectangle = new fabric.Rect({
          left:
            this.originX > activeObjectWidth ? activeObjectWidth : this.originX,
          top:
            this.originY > activeObjectHeight
              ? activeObjectHeight
              : this.originY,
          width: rectWidth,
          height: rectHeight,
          strokeWidth: 0,
          fill: "lightblue",
          opacity: 0.5,
          hasRotatingPoint: false,
          lockScalingFlip: true
        });

        this.fabricCanvasElement.add(rectangle);
        this.fabricCanvasElement.discardActiveObject();
        this.fabricCanvasElement.setActiveObject(rectangle);
        this.fabricCanvasElement.renderAll();
        this.isStartCreate = false;

        const activeObject = this.fabricCanvasElement.getActiveObject();
        this.setDataInInputArea(activeObject);
        this.existActiveObject = true;

        this.currentSelectObject = this.fabricCanvasElement.getActiveObject();
      }
    },
    mouseMoving(event) {
      const activeObject = event.target;
      const canvas = activeObject.canvas;
      const top = activeObject.top;
      const left = activeObject.left;
      const canvasWidth = canvas.width / canvas.getZoom();
      const canvasHeight = canvas.height / canvas.getZoom();
      const activeObjectWidth = activeObject.width * activeObject.scaleX; // 現在表示している矩形の幅
      const activeObjectHeight = activeObject.height * activeObject.scaleY; // 現在表示している矩形の高さ

      // 左上の原点としているためtop、leftは0
      const topBound = 0;
      const leftBound = 0;
      const bottomBound = canvasHeight - activeObjectHeight; // キャンバスサイズから矩形の高さを除いた位置 (y座標の上限)
      const rightBound = canvasWidth - activeObjectWidth; // キャンバスサイズから矩形の幅を除いた位置（x座標の上限）

      activeObject.set({
        left: Math.min(Math.max(left, leftBound), rightBound)
      });
      activeObject.set({ top: Math.min(Math.max(top, topBound), bottomBound) });
    },
    mouseMoved() {
      this.isStartCreate = false;
    },
    mouseScaling(event) {
      this.isStartCreate = false;
      let activeObject = event.target;
      const canvas = activeObject.canvas;
      const canvasHeight = canvas.height / canvas.getZoom();
      const canvasWidth = canvas.width / canvas.getZoom();
      const totalWidth = activeObject.width * activeObject.scaleX;
      const totalHeight = activeObject.height * activeObject.scaleY;
      const topBound = 0;
      const bottomBound = canvasHeight;
      const leftBound = 0;
      const rightBound = canvasWidth;

      // 高さが描画範囲を超えたら
      if (Math.floor(activeObject.top) < topBound) {
        activeObject.scaleY = activeObject.canvas.lastScaleY;
        activeObject.set("top", topBound);
      }
      if (Math.floor(activeObject.top + totalHeight) > bottomBound) {
        activeObject.scaleY = activeObject.canvas.lastScaleY;
      }

      // 幅が描画範囲を超えたら
      if (Math.floor(activeObject.left) < leftBound) {
        activeObject.scaleX = activeObject.canvas.lastScaleX;
        activeObject.set("left", leftBound);
      }
      if (Math.floor(activeObject.left + totalWidth) > rightBound) {
        activeObject.scaleX = activeObject.canvas.lastScaleX;
      }

      activeObject.canvas.lastScaleY = activeObject.scaleY;
      activeObject.canvas.lastScaleX = activeObject.scaleX;
    },
    mouseScaled(event) {
      let activeObject = event.target;
      const rectStartPoint = activeObject.aCoords.tl;
      const rectEndPoint = activeObject.aCoords.br;
      const rectWidth = rectEndPoint.x - rectStartPoint.x;
      const rectHeight = rectEndPoint.y - rectStartPoint.y;
      activeObject.set("width", rectWidth);
      activeObject.set("height", rectHeight);
      activeObject.set("scaleX", 1);
      activeObject.set("scaleY", 1);
      activeObject.set("lastScaleY", 1);
      activeObject.set("lastScaleY", 1);
    },
    /* ------------------------------------------------------------------ */
    checkUrl(value) {
      const regex = new RegExp(/^https?:\/\//);
      const checkResult = regex.test(value);
      if (checkResult) {
        this.errorMessageInvalidUrl = "";
        this.isInvalidUrl = false;
      } else {
        this.errorMessageInvalidUrl = this.validationLang.replace(
          ":attribute",
          "URL"
        );
        this.isInvalidUrl = true;
      }
      const activeObject = this.fabricCanvasElement.getActiveObject();
      activeObject.set("linkUrl", value);
      activeObject.set("linkPageInContents", null);

      return checkResult;
    },
    getContentsPageImage(dataUrl, index) {
      // fabric.jsのキャンバス内の初期化
      this.fabricCanvasElement.clear();

      fabric.Image.fromURL(dataUrl, img => {
        this.currentImageOriginWidth = img.width;
        this.currentImageOriginHeight = img.height;
        this.currentImageDataUrl = dataUrl;
        this.currentContentsPage = index;

        this.resizeCanvas();
        const resizeRate = this.currentImageOriginWidth / this.resizeWidth;
        let linkObject = [];
        this.draftContentsLinks.forEach(link => {
          if (link.page === index) {
            const currentObjectWidth = Math.floor(link.width / resizeRate);
            const currentObjectHeight = Math.floor(link.height / resizeRate);
            const currentObjectTop = Math.floor(link.y / resizeRate);
            const currentObjectLeft = Math.floor(link.x / resizeRate);

            const rectangle = new fabric.Rect({
              left: currentObjectLeft,
              top: currentObjectTop,
              width: currentObjectWidth,
              height: currentObjectHeight,
              strokeWidth: 0,
              fill: link.isUnpublished ? "lightblue" : "blue",
              opacity: 0.5,
              hasRotatingPoint: false,
              lockScalingFlip: true,
              linkId: link.linkId,
              linkUrl: link.linkType === "url" ? link.linkUrl : null,
              linkPageInContents:
                link.linkType === "page_in_contents"
                  ? Number(link.linkPageInContents)
                  : null,
              linkType: link.linkType
            });

            this.fabricCanvasElement.add(rectangle);
            linkObject.push(rectangle);
          }
        });
        if (linkObject.length > 0) {
          this.fabricCanvasElement.renderAll();
        }
        this.isShowSelectPageDialog = false;
      });
      this.changedPages = true;

      // 初期表示時はフラグを無効にする
      if (this.isInitialDisplay) {
        this.changedPages = false;
        this.isInitialDisplay = false;
      }
    },
    initialData() {
      this.currentSelectObject = null;
      this.destinationPage = null;
      this.embedLinkUrl = "";
      this.isClearedSelect = false;
      this.isDisabled = true;
      this.isDelete = false;
    },
    isNil(value) {
      return _.isNil(value);
    },
    unscrambleImages(images) {
      const tasks = [];
      images.forEach((image, index) => {
        tasks.push(
          new Promise((resolve, reject) => {
            try {
              const tmpImageElement = new Image();
              tmpImageElement.crossOrigin = "Anonymous";
              tmpImageElement.src = image.imageUrl;
              const tmpCanvasElement = document.createElement("canvas");
              tmpImageElement.onload = () => {
                doUnscramble(
                  image.scramble,
                  this.scrambleWidth,
                  this.scrambleHeight,
                  this.resolution,
                  tmpImageElement,
                  tmpCanvasElement
                );
                image.dataUrl = tmpCanvasElement.toDataURL();
                this.contentsPageImages[index] = image;
                resolve();
              };
            } catch (e) {
              reject(e);
            }
          })
        );
      });
      Promise.all(tasks)
        .then(() => {
          const index =
            this.isFrontCoverDefaultSetting && this.isWebUploadContents ? 1 : 0;
          this.getContentsPageImage(
            this.contentsPageImages[index].dataUrl,
            index
          );
          this.isNothingContentsData = false;
        })
        .catch(error => {
          log.error("error:", error);
        });
    },
    onClickSaveButton() {
      if (this.linkType === "url" && !this.checkUrl(this.embedLinkUrl)) {
        return;
      }

      if (
        this.linkType === "page_in_contents" &&
        this.isNil(this.destinationPage)
      ) {
        this.errorMessageNoPageSelected = this.lang.error_no_page_selected;
        return;
      }

      this.isSaveLink = true;

      const resizeRate = this.currentImageOriginWidth / this.resizeWidth;
      let currentObject = this.fabricCanvasElement.getActiveObject();

      const currentObjectWidth = Math.floor(currentObject.width * resizeRate);
      const currentObjectHeight = Math.floor(currentObject.height * resizeRate);
      const currentObjectTop = Math.floor(currentObject.top * resizeRate);
      const currentObjectLeft = Math.floor(currentObject.left * resizeRate);

      let requestData = {
        x: currentObjectLeft,
        y: currentObjectTop,
        width: currentObjectWidth,
        height: currentObjectHeight,
        linkType: this.linkType
      };

      const isLinkTypeUrl = this.linkType === "url";
      if (isLinkTypeUrl) {
        requestData.linkUrl = this.embedLinkUrl;
      }
      const isLinkTypeContentsPage = this.linkType === "page_in_contents";
      if (isLinkTypeContentsPage) {
        requestData.linkPageInContents = this.destinationPage;
      }

      // リンク新規作成
      if (!currentObject.linkId) {
        requestData.draftId = this.draftId;
        requestData.page = this.currentContentsPage;
        this.postContentsAddLink(requestData).then(res => {
          if (res.status !== 0) {
            this.isSaveLink = false;
            return;
          }

          requestData.linkId = res.data.linkId;
          requestData.isUnpublished = true;
          this.draftContentsLinks.push(requestData);
          let fabricObjects = this.fabricCanvasElement.getObjects();
          fabricObjects.forEach(object => {
            if (!object.linkId) {
              object.set({
                linkId: res.data.linkId,
                linkType: requestData.linkType,
                linkUrl: isLinkTypeUrl ? this.embedLinkUrl : null,
                linkPageInContents: isLinkTypeContentsPage
                  ? this.destinationPage
                  : null,
                fill: "lightblue"
              });
            }
          });
          this.fabricCanvasElement.renderAll();
          this.isSaveLink = false;
        });
      }

      // リンク更新
      if (currentObject.linkId) {
        requestData.linkId = currentObject.linkId;
        this.saveContentsAddLink(requestData).then(res => {
          if (res !== 0) {
            this.isSaveLink = false;
            return;
          }
          this.draftContentsLinks.map(link => {
            if (link.linkId === requestData.linkId) {
              link.x = requestData.x;
              link.y = requestData.y;
              link.width = requestData.width;
              link.height = requestData.height;
              link.linkType = requestData.linkType;
              link.linkUrl = isLinkTypeUrl ? this.embedLinkUrl : null;
              link.linkPageInContents = isLinkTypeContentsPage
                ? this.destinationPage
                : null;
              link.isUnpublished = true;
            } else {
              return link;
            }
          });
          let fabricObjects = this.fabricCanvasElement.getObjects();
          fabricObjects.forEach(object => {
            if (object.linkId === requestData.linkId) {
              object.set({
                linkType: requestData.linkType,
                linkUrl: isLinkTypeUrl ? this.embedLinkUrl : null,
                linkPageInContents: isLinkTypeContentsPage
                  ? this.destinationPage
                  : null,
                fill: "lightblue"
              });
            }
          });
          this.fabricCanvasElement.renderAll();
          this.isSaveLink = false;
        });
      }
      this.isEdit = true;
    },
    onClickDeleteButton() {
      this.isDelete = true;
      const activeObject = this.fabricCanvasElement.getActiveObject();

      if (!activeObject.linkId) {
        this.initialData();
        this.fabricCanvasElement.remove(activeObject);
      }

      if (activeObject.linkId) {
        let currentLinkId = activeObject.linkId;
        const requestData = {
          linkId: currentLinkId
        };
        this.deleteContentsAddLink(requestData).then(res => {
          if (res === 0) {
            this.draftContentsLinks = this.draftContentsLinks.filter(
              link => link.linkId !== requestData.linkId
            );
            this.initialData();
            this.fabricCanvasElement.remove(activeObject);
            this.isEdit = true;
          }
        });
      }
      this.errorMessageInvalidUrl = "";
      this.isInvalidUrl = false;
    },
    onClickSaveSettingButton() {
      this.isSaveSetting = true;
      const requestData = {
        draftId: this.draftId
      };
      this.saveContentsLinkSettings(requestData).then(status => {
        if (status === 0) {
          // 完了画面に遷移するときにアラートがでてしまうため、中身を空にする
          window.onbeforeunload = () => {};
          window.location.href = this.addLinkCompleteUrl;
        } else {
          this.isSaveSetting = false;
        }
      });
    },
    onClickGeneratePreview() {
      this.isGeneratePreview = true;
      const requestData = {
        draftId: this.draftId
      };
      this.saveContentsLinkSettingsPreview(requestData).then(res => {
        if (res.status !== 0) {
          this.isGeneratePreview = false;
          return;
        }

        this.isEdit = false;
        this.isGeneratePreview = false;
        this.openUrl(res.viewerUrl);
      });
    },
    openUrl(url) {
      if (ice.isIce()) {
        ice.openUrl(url, true);
      } else {
        window.open(url);
      }
    },
    updateIsShowSelectPageDialog(value) {
      this.isDisabled = true;
      this.destinationPage = null;
      this.embedLinkUrl = "";
      this.isShowSelectPageDialog = value;
    },
    updateIsShowSelectJumpPageDialog(value) {
      const activeObject = this.fabricCanvasElement.getActiveObject();
      activeObject.set("linkUrl", null);
      activeObject.set("linkPageInContents", this.destinationPage);
      this.isShowSelectJumpPageDialog = value;
    },
    resizeCanvas() {
      const resizeFunc = resizeSize => {
        const resizeRate = this.resizeWidth / resizeSize;
        this.resizeWidth = resizeSize;
        fabric.Image.fromURL(this.currentImageDataUrl, img => {
          this.resizeHeight =
            (this.resizeWidth * this.currentImageOriginHeight) /
            this.currentImageOriginWidth;
          this.fabricCanvasElement.setWidth(this.resizeWidth);
          this.fabricCanvasElement.setHeight(this.resizeHeight);
          this.fabricCanvasElement.renderAll.bind(this.fabricCanvasElement);
          this.fabricCanvasElement.setBackgroundImage(
            img,
            this.fabricCanvasElement.renderAll.bind(this.fabricCanvasElement),
            {
              scaleX: this.fabricCanvasElement.width / img.width,
              scaleY: this.fabricCanvasElement.height / img.height
            }
          );
        });
        const fabricObjects = this.fabricCanvasElement.getObjects();
        this.fabricCanvasElement.clear();
        fabricObjects.forEach(object => {
          const rectangle = new fabric.Rect({
            left: object.left / resizeRate,
            top: object.top / resizeRate,
            width: object.width / resizeRate,
            height: object.height / resizeRate,
            strokeWidth: 0,
            fill: object.fill,
            opacity: 0.5,
            hasRotatingPoint: false,
            lockScalingFlip: true,
            linkId: object.linkId,
            linkUrl: object.linkUrl,
            linkPageInContents: object.linkPageInContents,
            linkType: object.linkType
          });
          this.fabricCanvasElement.add(rectangle);
          this.fabricCanvasElement.renderAll();
          this.initialData();
        });
      };

      switch (this.$vuetify.breakpoint.name) {
        case "xs":
          resizeFunc(IMAGE_SIZE_XS);
          break;
        case "sm":
        case "md":
        case "lg":
        case "xl":
        default:
          resizeFunc(IMAGE_SIZE_MD);
          break;
      }
    },
    setDestinationPage(value) {
      this.destinationPage = value;
      this.isShowSelectJumpPageDialog = false;
      this.errorMessageNoPageSelected = "";
    },
    setDataInInputArea(activeObject) {
      this.isDisabled = false;
      this.embedLinkUrl = activeObject.linkUrl;
      this.linkType = activeObject.linkType ? activeObject.linkType : "url";
      this.destinationPage = activeObject.linkPageInContents;
    }
  },
  computed: {
    displayDestinationPage() {
      return this.lang.destination_page
        .replace(":max_num", this.contentsAllPageCount)
        .replace(":selected_num", this.destinationPage + 1);
    },
    deleteButtonWrapWidth() {
      switch (this.$vuetify.breakpoint.name) {
        case "xs":
          return { width: IMAGE_SIZE_XS + "px" };
        case "sm":
        case "md":
        case "lg":
        case "xl":
        default:
          return { width: IMAGE_SIZE_MD + "px" };
      }
    }
  }
};
</script>

<style module lang="scss">
.errorMessage {
  &__noPageSelected {
    color: red;
  }
}
.showImageListButton {
  height: 100%;
  padding: 12px;

  div {
    width: 100%;
  }

  &__text {
    white-space: normal;
  }
}
.inputAreaDisable {
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  background-color: black;
  opacity: 0.2;
}
.listImages {
  display: flex;
  flex-wrap: wrap;
  flex-direction: row;

  &__border {
    border: 1px solid blue;
  }
  &__listImage {
    cursor: pointer;
    border: black solid 1px;
  }
}
.canvasArea {
  max-width: 100%;
  margin-top: 16px;
  margin-bottom: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;

  canvas {
    border: 1px black solid;
  }
}
.progressArea {
  position: relative;
  min-height: 500px;

  &__loadingIcon {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
}
</style>
